// ─── AONE Design System — Component Layer v3 ────────────────────────────────
// Apple Pastel Studio Premium: quiet Apple surfaces, pastel module identity,
// and SAP-level information density for operator workflows.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MobileRecordCard, pickCardFields } from './mobileRecordCard';
import { fieldLabel } from './fieldLabel';
import { useNavigate } from 'react-router';
import { useResponsive } from '../hooks/useResponsive';
import { T, resolveStatus } from './tokens';
import { useFileToken, fileSrc } from '../utils/fileToken';
import { AoneAIInsight } from './ai';

const base = { fontFamily: T.font, boxSizing: 'border-box' };

// Per-module accent. Resolves to the CSS variable set by ModuleThemeProvider
// (config/moduleTheme.js), falling back to bronze so any usage outside a
// provider — or a dashboard route without a scoped module accent — still
// receives the Angna bronze default. Use ACCENT for SOLID colour usages only (background, border,
// color, fill, accentColor). Never string-concatenate it with a hex suffix
// (e.g. `${ACCENT}18`) — that path still needs a literal hex (see AoneKpiCard).
const ACCENT = 'var(--aone-accent, #6E1F2B)';
const ACCENT_HOVER = 'var(--aone-accent-hover, #591722)';
const ACCENT_SOFT = 'var(--aone-accent-soft, #F3E7E6)';
const ACCENT_MID = 'var(--aone-accent-mid, rgba(110, 31, 43,0.18))';

function merge(...styles) {
  return Object.assign({}, ...styles.filter(Boolean));
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. AonePageShell — Fusion C2 Object Header (SAP density × Apple polish)
// New optional props:
//   - iconTile: { letter, gradient: [from, to], inkColor }  → 42px gradient tile
//   - status:   { label, role }                              → animated live chip
//   - meta:     [ "Plant 01", "Week 21", ... ]               → inline meta row
//   - kpis:     [ { label, value, unit, trend, role } ]      → dense pastel ribbon
//   - footerBar:{ left, actions }                            → sticky bottom toolbar
//   - breadcrumb: [{ label, path }]                          → preserved
//   - title/subtitle/actions/alert                           → preserved
// ─────────────────────────────────────────────────────────────────────────────
export function AonePageShell({
  title, subtitle, breadcrumb, actions, alert, children, noPad = false, style,
  iconTile, status, meta, kpis, footerBar, aiInsight, mobileFab,
}) {
  const { isMobile, isTablet } = useResponsive();
  const nav = useNavigate();
  const pad = noPad ? 0 : isMobile ? '12px 12px 92px' : isTablet ? '16px 18px 84px' : '20px 24px 88px';

  const hasObjHeader = title || iconTile || kpis;

  return (
    <div
      className="aone-studio-shell"
      style={merge({
        ...base,
        // 100% of the scroll container, not 100vh — see FioriPage in fiori.js.
        // This shell is currently shadowed by the fioriBridge export, but a
        // duplicate carrying the same bug is how it comes back.
        minHeight: '100%',
        background: T.bg,
        backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.74) 0%, rgba(247,249,247,0) 42%)',
        padding: pad,
        color: T.text,
      }, style)}
    >
      {breadcrumb?.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          {breadcrumb.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ fontSize: T.type.xs, color: T.textMuted }}>›</span>}
              <span
                onClick={crumb.path ? () => nav(crumb.path) : undefined}
                style={{ fontSize: T.type.xs, color: crumb.path ? ACCENT : T.textSec, fontWeight: 600, cursor: crumb.path ? 'pointer' : 'default', fontFamily: T.font }}
              >{crumb.label}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {alert && <div style={{ marginBottom: 14 }}>{alert}</div>}

      {hasObjHeader && (
        <div className="aone-studio-header" style={{
          position: 'relative', overflow: 'hidden',
          background: 'rgba(255,255,255,0.94)',
          backdropFilter: 'blur(18px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.4)',
          borderRadius: isMobile ? T.r.lg : T.r.xl,
          border: `1px solid ${T.border}`,
          boxShadow: T.shadow.lg,
          padding: isMobile ? '14px 14px 12px' : isTablet ? '16px 18px 14px' : '16px 22px 14px',
          marginBottom: isMobile ? 12 : 16,
        }}>
          {/* Per-module accent identity — top spine + soft corner glow, auto-coloured
              by the active module's --aone-accent (set by ModuleThemeProvider). */}
          <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: 'linear-gradient(90deg, var(--aone-accent, #6E1F2B), var(--aone-accent-mid, rgba(110, 31, 43,0.18)) 65%, transparent)' }} />
          <div aria-hidden="true" style={{ position: 'absolute', top: -40, right: -30, width: 180, height: 180, pointerEvents: 'none',
            background: 'radial-gradient(circle, var(--aone-accent-soft, rgba(110, 31, 43,0.12)) 0%, transparent 68%)', opacity: 0.7 }} />

          {/* Top row: title block + actions */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            gap: isMobile ? 12 : 20, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? 0 : 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {(iconTile || title) && (
                  <div style={{
                    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                    // Auto monogram — always the module's own accent (unless a page
                    // passes an explicit gradient), so the tile matches the accent spine.
                    background: iconTile?.gradient
                      ? `linear-gradient(135deg, ${iconTile.gradient[0]} 0%, ${iconTile.gradient[1]} 100%)`
                      : 'linear-gradient(135deg, var(--aone-accent, #6E1F2B) 0%, color-mix(in srgb, var(--aone-accent, #6E1F2B) 76%, #000) 100%)',
                    color: iconTile?.inkColor || '#FFFFFF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: T.type.lg, fontWeight: 800,
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 6px rgba(0,0,0,0.10)',
                  }}>{iconTile?.letter || (title ? String(title).trim().charAt(0).toUpperCase() : '•')}</div>
                )}
                <div>
                  {title && (
                    <h1 style={{ margin: 0, fontSize: isMobile ? T.type.xxl : T.type.xxxl, fontWeight: 600, color: T.text, letterSpacing: '-0.01em', fontFamily: T.fontDisplay, lineHeight: 1.14 }}>
                      {title}
                    </h1>
                  )}
                  {(meta?.length || status) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, fontSize: T.type.sm, color: T.textTer, flexWrap: 'wrap' }}>
                      {meta?.map((m, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span style={{ color: T.textMuted }}>·</span>}
                          <span>{m}</span>
                        </React.Fragment>
                      ))}
                      {status && (
                        <>
                          {(meta?.length > 0) && <span style={{ color: T.textMuted }}>·</span>}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 10px', borderRadius: 100,
                            fontSize: T.type.xs, fontWeight: 700,
                            background: T.role[status.role || 'mint'].fill,
                            color: T.role[status.role || 'mint'].ink,
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animation: 'aonePulse 2s ease-in-out infinite' }} />
                            {status.label}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {((actions && !(isMobile && mobileFab)) || (kpis?.length > 0 && !isMobile)) && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center', justifyContent: isMobile ? 'stretch' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                {/* Command strip (2026-09-13): kpis render as right-aligned mono
                    tiles beside the actions, click-to-filter when a kpi carries
                    onClick. Replaces the pastel ribbon below the title. */}
                {kpis?.length > 0 && !isMobile && (
                  <div style={{ display: 'flex', marginRight: actions ? 6 : 0 }}>
                    {kpis.map((k, i) => {
                      const tone = k.tone || (k.role === 'coral' || k.role === 'blush' ? T.danger : k.role === 'lemon' || k.role === 'apricot' ? T.warning : k.role === 'mint' || k.role === 'sage' ? T.success : T.text);
                      return (
                        <div key={i} onClick={k.onClick} title={k.onClick ? 'Click to filter' : (k.trend || undefined)}
                          style={{ padding: '2px 14px', borderLeft: i ? `1px solid ${T.divider}` : 'none', textAlign: 'right', cursor: k.onClick ? 'pointer' : 'default', background: k.active ? T.accentSoft : 'transparent', borderRadius: 8 }}>
                          <div style={{ fontFamily: T.fontMono, fontSize: T.type.lg, fontWeight: 800, color: tone, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                            {typeof k.value === 'number' ? k.value.toLocaleString('en-IN') : k.value}{k.unit && <span style={{ fontSize: T.type.xs, fontWeight: 700, marginLeft: 3, opacity: 0.7 }}>{k.unit}</span>}
                          </div>
                          <div style={{ fontSize: T.type.xxs, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: T.textMuted, marginTop: 4, whiteSpace: 'nowrap' }}>{k.label}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {actions}
              </div>
            )}
          </div>

          {/* AI insight slot — a consistent home for module smartness */}
          {aiInsight && (
            <div style={{ position: 'relative', marginTop: 14 }}>
              {typeof aiInsight === 'string' ? <AoneAIInsight text={aiInsight} /> : aiInsight}
            </div>
          )}

          {/* On a phone, kpis are a HEADLINE, not a grid.
              Four tiles at 375px either clip mid-word or eat the screen before
              a single record — which is what Purchase Orders did. A phone
              carries one number you came for and, when something is wrong, one
              number that says so. `headline` on a kpi forces the big slot;
              a danger tone picks the right-hand one. All of them stay
              available to the desktop command strip. */}
          {kpis?.length > 0 && isMobile && (() => {
            const big = kpis.find((k) => k.headline) || kpis[0];
            const alarm = kpis.find((k) => k !== big
              && (k.tone === T.danger || k.role === 'coral' || k.role === 'blush')
              && Number(k.value) !== 0);
            const show = (k) => (typeof k.value === 'number' ? k.value.toLocaleString('en-IN') : k.value);
            return (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 12 }}>
                <div onClick={big.onClick} style={{ cursor: big.onClick ? 'pointer' : 'default', minWidth: 0 }}>
                  <span style={{ fontSize: T.type.huge, fontWeight: 820, letterSpacing: '-0.02em', lineHeight: 1, color: T.text, fontVariantNumeric: 'tabular-nums' }}>
                    {show(big)}
                  </span>
                  <span style={{ fontSize: T.type.sm, color: T.textMuted, fontWeight: 600, marginLeft: 7 }}>
                    {big.unit || big.label}
                  </span>
                </div>
                {alarm && (
                  <div onClick={alarm.onClick} style={{ marginLeft: 'auto', textAlign: 'right', cursor: alarm.onClick ? 'pointer' : 'default', flexShrink: 0 }}>
                    <div style={{ fontSize: T.type.lg, fontWeight: 800, color: T.danger, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                      {show(alarm)}
                    </div>
                    <div style={{ fontSize: T.type.xxs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: T.textMuted }}>
                      {alarm.label}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* The one action a phone needs, where a thumb reaches. Passing
          mobileFab also suppresses the desktop action row on mobile — three
          text buttons stacked above the list is desktop furniture. */}
      {isMobile && mobileFab && (
        <div
          onClick={mobileFab.onClick}
          role="button"
          aria-label={mobileFab.label || 'Add'}
          style={{
            position: 'fixed', right: 16, bottom: 78, width: 52, height: 52, borderRadius: '50%',
            background: ACCENT, color: '#fff', display: 'grid', placeItems: 'center',
            fontSize: 26, fontWeight: 300, lineHeight: 1, cursor: 'pointer', zIndex: 40,
            boxShadow: '0 8px 20px rgba(110,31,43,0.36)',
          }}
        >{mobileFab.icon || '+'}</div>
      )}

      {/* Children render area */}
      {children}

      {/* footerBar retired 2026-07-30: the sticky bottom toolbar duplicated
          header KPIs/actions and ate viewport height on every module. The
          prop is still accepted (and ignored) so old callers don't crash. */}

      {/* Keyframes for pulse — injected once via style tag */}
      <style>{`
        @keyframes aonePulse { 50% { opacity: 0.4; } }
        .aone-studio-shell * { box-sizing: border-box; }
        @media (max-width: 767px) {
          .aone-studio-header button,
          .aone-studio-header a { min-height: 38px; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1b. AoneKpiRibbon — standalone dense pastel KPI strip (when not in PageShell)
// ─────────────────────────────────────────────────────────────────────────────
export function AoneKpiRibbon({ kpis, style }) {
  const { isMobile } = useResponsive();
  if (!kpis?.length) return null;
  return (
    <div style={merge(base, {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${kpis.length}, 1fr)`,
      gap: 8, marginBottom: 14,
    }, style)}>
      {kpis.map((k, i) => {
        const r = T.role[k.role] || T.role.lilac;
        return (
          <div key={i} style={{
            padding: '12px 14px', borderRadius: 10,
            background: r.fill, border: `1px solid ${r.ink}1F`,
          }}>
            <div style={{ fontSize: T.type.xxs, fontWeight: 700, letterSpacing: 0, textTransform: 'uppercase', color: r.ink, marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: T.type.xxxl, fontWeight: 800, letterSpacing: 0, color: T.text, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {k.value}{k.unit && <span style={{ fontSize: T.type.xs, fontWeight: 700, marginLeft: 4, opacity: 0.7 }}>{k.unit}</span>}
            </div>
            {k.trend && (
              <div style={{ marginTop: 5, fontSize: T.type.xs, fontWeight: 600, color: r.ink, opacity: 0.85 }}>{k.trend}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1c. AoneFilterChip — toolbar filter chip (on/off state, × hint on active)
// ─────────────────────────────────────────────────────────────────────────────
export function AoneFilterChip({ active, onClick, role = 'lilac', children, style }) {
  const r = T.role[role] || T.role.lilac;
  return (
    <button onClick={onClick} style={merge(base, {
      padding: '5px 10px', borderRadius: 100, cursor: 'pointer',
      background: active ? r.fill : T.surface,
      border: active ? '1px solid transparent' : `1px solid ${T.border}`,
      color: active ? r.ink : T.textSec,
      fontSize: T.type.xs, fontWeight: active ? 700 : 600,
      display: 'inline-flex', alignItems: 'center', gap: 5,
    }, style)}>
      {children}
      {active && <span style={{ opacity: 0.65 }}>×</span>}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. AoneCard — premium pastel card with optional accent strip
// variant: 'default' | 'rose' | 'blush' | 'gold' | 'oud' | 'aqua' | 'lavender' | 'flat' | 'ghost'
// ─────────────────────────────────────────────────────────────────────────────
export function AoneCard({
  children, title, subtitle, headerRight,
  variant = 'default', padding, onClick, style, bodyStyle, accentTop = false,
}) {
  const { isMobile } = useResponsive();
  const defaultPad = padding ?? (isMobile ? '16px' : '20px 22px');

  const tlcVariants = ['rose', 'blush', 'gold', 'oud', 'aqua', 'lavender', 'terra', 'sage', 'sand', 'lilac', 'sky', 'mint', 'peach', 'coral', 'apricot'];
  const isTlc = tlcVariants.includes(variant);
  const tlc = T.card[variant];

  const cardStyle = isTlc
    ? { background: tlc.bg, border: `1px solid ${tlc.border}`, boxShadow: T.shadow.sm }
    : variant === 'flat'
    ? { background: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow.sm }
    : variant === 'ghost'
    ? { background: 'transparent', border: `1px dashed ${T.border}`, boxShadow: 'none' }
    : { background: 'rgba(255,255,255,0.96)', border: `1px solid ${T.border}`, boxShadow: T.shadow.md };

  const accentColor = isTlc ? tlc.accent : ACCENT;

  return (
    <div
      onClick={onClick}
      style={merge(base, {
        borderRadius: T.r.lg, overflow: 'hidden',
        transition: `box-shadow ${T.duration.slow} ${T.ease}, transform ${T.duration.slow} ${T.spring}`,
        position: 'relative',
      }, cardStyle, onClick && { cursor: 'pointer' }, style)}
      onMouseEnter={onClick ? e => { e.currentTarget.style.boxShadow = T.shadow.hover; e.currentTarget.style.transform = 'scale(1.02) translateY(-2px)'; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.boxShadow = cardStyle.boxShadow; e.currentTarget.style.transform = 'none'; } : undefined}
    >
      {/* Top accent stripe */}
      {accentTop && (
        <div style={{ height: 3, background: accentColor, borderRadius: `${T.r.lg}px ${T.r.lg}px 0 0` }} />
      )}

      {(title || headerRight) && (
        <div style={{ paddingTop: defaultPad, paddingRight: defaultPad, paddingLeft: defaultPad, paddingBottom: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            {title && <div style={{ fontSize: isMobile ? T.type.md : T.type.lg, fontWeight: 760, color: T.text, fontFamily: T.font, letterSpacing: 0, lineHeight: 1.2 }}>{title}</div>}
            {subtitle && <div style={{ fontSize: T.type.sm, color: T.textSec, marginTop: 3, fontFamily: T.font, lineHeight: 1.4 }}>{subtitle}</div>}
          </div>
          {headerRight}
        </div>
      )}
      <div style={merge({ padding: defaultPad }, bodyStyle)}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. AoneKpiCard — SAP metric card with Apple pastel softness
// ─────────────────────────────────────────────────────────────────────────────
export function AoneKpiCard({
  label, value, delta, deltaLabel, icon, sub, trend, tone,
  color,     // override accent color
  variant,   // card variant
  onClick, style,
}) {
  // Command strip (2026-09-13): the remaining card grids (section stats,
  // record panels, pages the codemod could not lift) draw in the same
  // vocabulary as the header tiles — mono number, small uppercase label, one
  // hairline — instead of the pastel card with an icon bubble. Same API.
  const tlcMap = { rose: T.rose, blush: T.blush, gold: T.gold, oud: T.oud, aqua: T.aqua, lavender: T.lavender, terra: T.terra, sage: T.sage, sand: T.sand, lilac: T.lilac, sky: T.sky, mint: T.accent, peach: T.warning, coral: T.danger, apricot: T.gold };
  const toneMap = { critical: T.danger, danger: T.danger, warning: T.warning, warn: T.warning, positive: T.success, success: T.success, ok: T.success, info: T.info };
  const accentColor = color || (tone && (toneMap[tone] || tone)) || (variant ? tlcMap[variant] : null);
  const deltaPositive = delta && String(delta)[0] !== '-';
  const deltaColor = delta ? (deltaPositive ? T.success : T.danger) : T.textSec;
  const note = sub || trend || deltaLabel;
  void icon;

  return (
    <div
      onClick={onClick}
      style={merge(base, {
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: '8px 14px',
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: `background ${T.duration.fast} ${T.ease}`,
      }, style)}
      onMouseEnter={onClick ? e => { e.currentTarget.style.background = T.accentSoft; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.background = T.surface; } : undefined}
    >
      <div style={{ fontFamily: T.fontMono, fontSize: T.type.lg, fontWeight: 800, color: accentColor || T.text, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
        {delta && <span style={{ fontSize: T.type.xs, fontWeight: 700, color: deltaColor, marginLeft: 6 }}>{deltaPositive && '+'}{delta}</span>}
      </div>
      <div style={{ fontSize: T.type.xxs, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: T.textMuted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      {note && <div style={{ fontSize: T.type.xs, color: T.textSec, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. AoneStatusChip — Fusion C2 filled pastel pill with role-locked palette
// ─────────────────────────────────────────────────────────────────────────────
export function AoneStatusChip({ status, label, size = 'md', style }) {
  const cfg = resolveStatus(status);
  const displayLabel = label || (String(status).charAt(0).toUpperCase() + String(status).slice(1));
  const sm = size === 'sm';

  return (
    <span style={merge(base, {
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: sm ? '2px 9px' : '3px 10px',
      background: cfg.bg,
      border: 'none',
      borderRadius: 100,
      fontSize: sm ? T.type.xxs : T.type.xs,
      fontWeight: 700,
      color: cfg.fg,
      letterSpacing: 0,
      whiteSpace: 'nowrap',
    }, style)}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.fg, opacity: 0.75, flexShrink: 0 }} />
      {displayLabel}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4a. AoneCopyBtn — tiny inline copy-to-clipboard button for IDs (AWB/tracking
// numbers etc.) so operators can paste them into WhatsApp/email without
// retyping. Renders nothing when there's no text to copy.
// ─────────────────────────────────────────────────────────────────────────────
export function AoneCopyBtn({ text, title = 'Copy', size = 14, style }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const copy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const value = String(text);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard unavailable — leave the button as-is */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied!' : `${title} ${String(text)}`}
      aria-label={copied ? 'Copied' : `Copy ${String(text)}`}
      style={merge(base, {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: size + 6, height: size + 6, padding: 0, marginLeft: 4,
        border: 'none', borderRadius: 5, cursor: 'pointer',
        background: copied ? T.role?.mint?.bg || '#E5F3EA' : 'transparent',
        color: copied ? T.role?.mint?.ink || '#1F7A46' : T.textSec,
        fontSize: size - 2, lineHeight: 1, verticalAlign: 'middle', flexShrink: 0,
      }, style)}
    >
      {copied ? '✓' : (
        <svg width={size - 3} height={size - 3} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
          <path d="M10.5 5.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5" />
        </svg>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4b. AoneAvatar — initials avatar, warm gradient (reuses T.module hero gradients
// so no new colours are introduced — same "Angna" palette as module icon tiles).
// Picks a gradient deterministically from `seed` (or `name`) so the same person
// always renders the same colour.
// ─────────────────────────────────────────────────────────────────────────────
const AVATAR_GRADIENT_KEYS = ['sales', 'procurement', 'production', 'finishedGoods', 'packaging', 'finance', 'rd', 'customerCare', 'hr', 'garnet'];
function hashSeed(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h;
}
export function AoneAvatar({ name = '', seed, src, size = 'md', shape = 'circle', style }) {
  // <img> can't send an Authorization header — see utils/fileToken.js
  const photoSrc = fileSrc(src, useFileToken());
  const px = { xs: 22, sm: 32, md: 38, lg: 54 }[size] || 38;
  const fontSize = { xs: 9.5, sm: 12, md: 14, lg: 20 }[size] || 14;
  const initials = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  const gradientKey = AVATAR_GRADIENT_KEYS[hashSeed(String(seed || name)) % AVATAR_GRADIENT_KEYS.length];

  const shell = merge(base, {
    width: px, height: px, flexShrink: 0,
    borderRadius: shape === 'circle' ? '50%' : T.r.md,
    background: T.module[gradientKey].hero,
    color: '#FFFFFF',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize, fontWeight: 800, letterSpacing: 0, overflow: 'hidden',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 2px 6px rgba(0,0,0,0.10)',
  }, style);

  // Initials always render as the base layer; a valid photo covers them, and
  // if it 404s onError hides the <img> so the initials show through instead
  // of a broken-image icon.
  return (
    <div style={{ ...shell, position: 'relative' }}>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials}</span>
      {src && (
        <img src={photoSrc} alt={name} onError={e => { e.currentTarget.style.display = 'none'; }}
          style={{ position: 'relative', width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. AoneDataTable — SAP-dense table with Apple responsive cards
// ─────────────────────────────────────────────────────────────────────────────
// ── Filter rail ──────────────────────────────────────────────────────────────
// One row of filters, scrollable, never wrapping.
//
// The Purchase Orders working row put five chips, a select and three buttons
// above the list; on a phone they wrapped to three rows and pushed the first
// record below the fold. A rail scrolls instead of wrapping, so the chrome
// costs one line whatever the filter count.
//
// Geometry is fixed rather than derived from the label: a pill radius clamps to
// half the height, so a two-character label ("All 36") comes out a circle. 34px
// tall, 10px radius, and the count is its own element in its own weight rather
// than bolded text riding along behind the label.
//
//   items: [{ key, label, count, tone: 'danger'|undefined, title }]
//   value / onChange: the selected key (null = the first item, "all")
export function AoneFilterRail({ items = [], value = null, onChange, style }) {
  // Items are keyed by `key`, but `value` is the obvious other guess and two
  // pages made it — every chip's key came out undefined, so they all reported
  // the same selection and tapping one filtered the list to nothing. Silent,
  // and it looks like a data problem. Accept both spellings, same as
  // AonePageShell does for breadcrumb/breadcrumbs.
  const live = items
    .filter((f) => f && f.count !== 0)
    .map((f) => (f.key === undefined && f.value !== undefined ? { ...f, key: f.value } : f));
  if (live.length <= 1) return null;
  return (
    <div
      style={merge({
        display: 'flex', gap: 8, overflowX: 'auto', padding: '0 0 2px',
        scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
      }, style)}
    >
      {live.map((f) => {
        const on = (value ?? live[0].key) === f.key;
        const alert = f.tone === 'danger' && !on;
        return (
          <button
            key={f.key}
            onClick={() => onChange?.(f.key)}
            aria-pressed={on}
            title={f.title || undefined}
            style={{
              flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 7,
              height: 34, padding: '0 13px', borderRadius: 10, cursor: 'pointer',
              fontFamily: T.font, fontSize: T.type.sm, fontWeight: 600, whiteSpace: 'nowrap',
              background: on ? T.text : (alert ? T.dangerBg : T.surface),
              color: on ? '#fff' : (alert ? T.danger : T.textSec),
              border: `1px solid ${on ? T.text : (alert ? '#E5C4C4' : T.border)}`,
            }}
          >
            {f.label}
            {f.count != null && (
              <span style={{
                fontWeight: 700, fontSize: T.type.xs, fontVariantNumeric: 'tabular-nums',
                color: on ? 'rgba(255,255,255,0.66)' : (alert ? T.danger : T.textTer),
              }}>{typeof f.count === 'number' ? f.count.toLocaleString('en-IN') : f.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Furniture that belongs to a desk. Exceptions bands, bulk-import buttons,
// print — real on a wide screen, noise on a phone where they push the records
// below the fold. Wrap rather than sprinkling isMobile through a page.
export function AoneDesktopOnly({ children }) {
  const { isMobile } = useResponsive();
  return isMobile ? null : children;
}

export function AoneDataTable({
  columns, rows, rowKey = 'id', getRowKey, onRowClick,
  selectable = false, selectedKeys = [], onSelectChange,
  sortKey, sortDir = 'asc', onSort,
  loading = false, empty, stickyHeader = true, style, maxHeight,
  hoverVariant = 'terra',  // semantic tint for row hover
  rowStyle,                // optional fn(row, idx) → extra <tr> style (status tinting, strike-through)
  rowActions,              // optional fn(row) → ReactNode rendered in a far-right cell
  footer,                  // optional bottom slot (pagination, totals)
}) {
  const { isMobile, isTablet } = useResponsive();
  const [internalSort, setInternalSort] = useState({ key: sortKey, dir: sortDir });

  // Column compat: `header` aliases `label`, `accessor` overrides row[key],
  // `numeric` right-aligns + tabular-nums.
  const colLabel = (col) => col.label ?? col.header;
  const colValue = (col, row) => (col.accessor ? col.accessor(row) : row[col.key]);
  const colAlign = (col) => col.align || (col.numeric ? 'right' : 'left');
  const keyOf = (row, i) => (getRowKey ? getRowKey(row, i) : row[rowKey] ?? i);

  const handleSort = (key) => {
    if (!key) return;
    const dir = internalSort.key === key && internalSort.dir === 'asc' ? 'desc' : 'asc';
    setInternalSort({ key, dir });
    onSort?.(key, dir);
  };

  const sortedRows = onSort ? rows : [...(rows || [])].sort((a, b) => {
    if (!internalSort.key) return 0;
    const col = columns.find(c => c.key === internalSort.key);
    const av = (col ? colValue(col, a) : a[internalSort.key]) ?? '';
    const bv = (col ? colValue(col, b) : b[internalSort.key]) ?? '';
    const cmp = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return internalSort.dir === 'asc' ? cmp : -cmp;
  });

  const isSelected = (row) => selectedKeys.includes(row[rowKey]);
  const toggleRow = (row) => {
    const key = row[rowKey];
    onSelectChange?.(isSelected(row) ? selectedKeys.filter(k => k !== key) : [...selectedKeys, key]);
  };
  const toggleAll = () => {
    if (selectedKeys.length === rows.length) onSelectChange?.([]);
    else onSelectChange?.(rows.map(r => r[rowKey]));
  };

  const hoverBg = 'rgba(120,120,128,0.04)';   // Fusion C2 — neutral row hover

  const thStyle = {
    padding: isTablet ? '10px 12px' : '11px 14px', textAlign: 'left', fontSize: T.type.xxs, fontWeight: 760,
    color: T.textTer, textTransform: 'uppercase', letterSpacing: 0,
    background: 'rgba(244,247,244,0.92)', borderBottom: `1px solid ${T.border}`,
    whiteSpace: 'nowrap', fontFamily: T.font,
  };

  if (loading) {
    return (
      <div style={merge(base, { background: T.surface, borderRadius: T.r.lg, border: `1px solid ${T.border}`, overflow: 'hidden', boxShadow: T.shadow.sm }, style)}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 12 }}>
            {[60, 140, 80, 100, 60].map((w, j) => (
              <div key={j} style={{ height: 14, width: w, borderRadius: 6, background: '#F3F4F6', animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Mobile: cards, from the shared renderer in ./mobileRecordCard — the same
  // one fiori.js uses, so the two AoneDataTable paths cannot drift.
  if (isMobile) {
    const fields = pickCardFields(columns, { colLabel, colValue });
    // See fiori.js deskTableStyle — a table's own width belongs to the table.
    const { minWidth: _mw, width: _w, maxWidth: _xw, ...cardStyle } = style || {};
    void _mw; void _w; void _xw;
    return (
      <div style={merge(base, { display: 'flex', flexDirection: 'column', gap: 8 }, cardStyle)}>
        {sortedRows.length === 0 && (empty || <AoneEmptyState message="No records found." />)}
        {sortedRows.map((row, ri) => {
          const f = fields(row);
          return (
            <MobileRecordCard
              key={keyOf(row, ri)}
              {...f}
              actions={f.actions || (rowActions ? rowActions(row) : null)}
              selected={isSelected(row)}
              accent={ACCENT}
              onOpen={onRowClick ? () => onRowClick(row) : null}
              extraStyle={rowStyle ? rowStyle(row, ri) : null}
            />
          );
        })}
        {footer && <div style={{ padding: '10px 4px', fontSize: T.type.xs, color: T.textSec, fontFamily: T.font }}>{footer}</div>}
      </div>
    );
  }

  return (
    <div style={merge(base, {
      background: 'rgba(255,255,255,0.96)', borderRadius: T.r.lg, border: `1px solid ${T.border}`,
      overflowX: 'auto', overflowY: maxHeight ? 'auto' : 'visible', boxShadow: T.shadow.sm, maxHeight,
      WebkitOverflowScrolling: 'touch',
    }, style)}>
      <table style={{ width: '100%', minWidth: isTablet ? 760 : undefined, borderCollapse: 'collapse', fontSize: T.type.md, fontFamily: T.font }}>
        <thead style={{ position: stickyHeader ? 'sticky' : undefined, top: stickyHeader ? 0 : undefined, zIndex: 2 }}>
          <tr>
            {selectable && (
              <th style={{ ...thStyle, width: 44, paddingLeft: 16 }}>
                <input type="checkbox" checked={rows.length > 0 && selectedKeys.length === rows.length}
                  onChange={toggleAll} style={{ cursor: 'pointer', accentColor: ACCENT }} />
              </th>
            )}
            {columns.map((col, ci) => (
              <th key={ci} onClick={col.sortable ? () => handleSort(col.key) : undefined}
                style={{
                  ...thStyle, width: col.width, textAlign: colAlign(col),
                  cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none',
                  borderLeft: ci === 0 && !selectable ? 'none' : `1px solid ${T.divider}`,
                }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {colLabel(col)}
                  {col.sortable && (
                    <span style={{ fontSize: T.type.xxs, color: internalSort.key === col.key ? ACCENT : '#9CA3AF' }}>
                      {internalSort.key === col.key ? (internalSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  )}
                </span>
              </th>
            ))}
            {rowActions && <th style={{ ...thStyle, width: 56, borderLeft: `1px solid ${T.divider}` }} aria-label="Row actions" />}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 && (
            <tr><td colSpan={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} style={{ padding: '48px 20px', textAlign: 'center' }}>
              {empty || <AoneEmptyState message="No records found." inline />}
            </td></tr>
          )}
          {sortedRows.map((row, ri) => {
            // Per-row status styling (cancelled strike-through, COD-received tint…)
            const extra = rowStyle ? rowStyle(row, ri) : null;
            const restingBg = (extra && extra.background) || (isSelected(row) ? ACCENT_SOFT : T.surface);
            return (
            <tr key={keyOf(row, ri)}
              onClick={() => onRowClick?.(row)}
              style={{
                borderTop: ri === 0 ? 'none' : `1px solid rgba(60,60,67,0.09)`,
                background: restingBg,
                cursor: onRowClick ? 'pointer' : 'default',
                transition: `background ${T.duration.fast}`,
                ...(extra || null),
              }}
              onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
              onMouseLeave={e => { e.currentTarget.style.background = restingBg; }}
            >
              {selectable && (
                <td style={{ padding: '11px 16px', width: 44 }} onClick={e => { e.stopPropagation(); toggleRow(row); }}>
                  <input type="checkbox" checked={isSelected(row)} onChange={() => toggleRow(row)}
                    style={{ cursor: 'pointer', accentColor: ACCENT }} />
                </td>
              )}
              {columns.map((col, ci) => {
                const v = colValue(col, row);
                return (
                <td key={ci} style={{
                  padding: isTablet ? '10px 12px' : '10px 14px', color: T.text, fontSize: T.type.sm,
                  textAlign: colAlign(col), verticalAlign: 'middle',
                  maxWidth: col.width || 'auto', fontWeight: 500,
                  fontVariantNumeric: col.numeric ? 'tabular-nums' : undefined,
                  borderLeft: ci === 0 && !selectable ? 'none' : `1px solid ${T.divider}`,
                }}>
                  {col.render ? col.render(v, row) : (v ?? <span style={{ color: T.textMuted }}>—</span>)}
                </td>
                );
              })}
              {rowActions && (
                <td onClick={e => e.stopPropagation()} style={{
                  padding: isTablet ? '10px 12px' : '10px 14px', width: 56, textAlign: 'right',
                  verticalAlign: 'middle', borderLeft: `1px solid ${T.divider}`,
                }}>
                  {rowActions(row)}
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
      {footer && (
        <div style={{
          padding: '12px 16px', borderTop: `1px solid ${T.border}`, background: 'rgba(244,247,244,0.6)',
          fontSize: T.type.sm, color: T.textSec, fontFamily: T.font,
        }}>
          {footer}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5b. AoneQuickAddRow — inline "create" row above a table (Fiori quick-add
// pattern). Trades the drawer/modal for a single dashed-accent bar of compact
// inputs + a submit button, so a one-line entry never leaves the table view.
// ─────────────────────────────────────────────────────────────────────────────
export function AoneQuickAddRow({ fields, onSubmit, onCancel, submitLabel = 'Add', submitting = false, style }) {
  const [values, setValues] = useState(() => Object.fromEntries(fields.map(f => [f.key, f.defaultValue ?? ''])));
  const set = (k, v) => setValues(p => ({ ...p, [k]: v }));
  const canSubmit = fields.every(f => f.required === false || (values[f.key] !== '' && values[f.key] != null));

  return (
    <div style={merge(base, {
      display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      padding: '10px 14px',
      background: ACCENT_SOFT,
      border: `1px dashed ${T.borderStrong}`,
      borderRadius: T.r.md,
      marginBottom: 10,
    }, style)}>
      {fields.map(f => {
        const inputStyle = {
          flex: f.flex || 1, minWidth: f.minWidth || 100,
          border: `1px solid ${T.border}`, borderRadius: T.r.sm,
          padding: '7px 10px', fontSize: T.type.sm, fontFamily: T.font,
          background: '#fff', color: T.text, outline: 'none',
        };
        if (f.type === 'select') {
          return (
            <select key={f.key} value={values[f.key]} onChange={e => set(f.key, e.target.value)} style={inputStyle}>
              <option value="">{f.placeholder || `— ${f.label} —`}</option>
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          );
        }
        return (
          <input
            key={f.key}
            type={f.type || 'text'}
            value={values[f.key]}
            onChange={e => set(f.key, e.target.value)}
            placeholder={f.placeholder || f.label}
            style={inputStyle}
          />
        );
      })}
      <AoneButton variant="primary" size="sm" onClick={() => onSubmit(values, setValues)} disabled={submitting || !canSubmit}>
        {submitting ? 'Saving…' : submitLabel}
      </AoneButton>
      {onCancel && <AoneButton variant="ghost" size="sm" onClick={onCancel}>Cancel</AoneButton>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. AoneRightPanel — right drawer / bottom sheet
// ─────────────────────────────────────────────────────────────────────────────
export function AoneRightPanel({ open, onClose, title, subtitle, children, actions, width = 480 }) {
  const { isMobile, isTablet } = useResponsive();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const isMobileStyle = isMobile || isTablet;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: T.overlay, zIndex: T.z.panel - 1,
        opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
        transition: `opacity ${T.duration.base} ${T.ease}`,
      }} />
      <div style={{
        position: 'fixed',
        ...(isMobileStyle
          ? { left: 0, right: 0, bottom: 0, borderRadius: `${T.r.xl}px ${T.r.xl}px 0 0`, transform: open ? 'translateY(0)' : 'translateY(100%)', maxHeight: '85vh' }
          : { top: 0, right: 0, bottom: 0, width, borderRadius: `${T.r.lg}px 0 0 ${T.r.lg}px`, transform: open ? 'translateX(0)' : 'translateX(100%)' }
        ),
        background: T.surface, boxShadow: T.shadow.xl,
        zIndex: T.z.panel, display: 'flex', flexDirection: 'column',
        transition: `transform ${T.duration.slow} ${T.easeOut}`,
        fontFamily: T.font,
      }}>
        {isMobileStyle && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4, flexShrink: 0 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border }} />
          </div>
        )}
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: T.type.lg, fontWeight: 700, color: '#000000' }}>{title}</div>
            {subtitle && <div style={{ fontSize: T.type.sm, color: '#111111', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: T.type.xxl, color: '#000000', padding: '4px 8px', borderRadius: T.r.sm, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px' }}>{children}</div>
        {actions && (
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. AoneActionBar — sticky search/filter toolbar
// ─────────────────────────────────────────────────────────────────────────────
// Top-level AoneActionBar search becomes a Google-style global search:
// keeps local-filter behavior (via search.onChange) AND shows a dropdown
// with cross-module results (clients, leads, SOs, formulations, etc.)
// hitting /api/dashboard/global-search as the user types.
export function AoneActionBar({
  search, filters, primary, secondary, bulkActions,
  selectedCount = 0, onClearSelection, exportFn, sticky = true, style,
}) {
  const { isMobile } = useResponsive();

  return (
    <div style={merge(base, {
      background: T.surface,
      borderRadius: T.r.md,
      border: `1px solid ${T.border}`,
      padding: isMobile ? '10px 14px' : '12px 18px',
      display: 'flex', alignItems: 'center', gap: 10,
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      boxShadow: T.shadow.sm,
      marginBottom: 16,
      ...(sticky ? { position: 'sticky', top: 0, zIndex: T.z.sticky } : {}),
    }, style)}>
      {selectedCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', background: ACCENT_SOFT, borderRadius: T.r.md, border: `1px solid ${ACCENT_MID}` }}>
          <span style={{ fontSize: T.type.sm, fontWeight: 700, color: '#000000' }}>{selectedCount} selected</span>
          <button onClick={onClearSelection} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: T.type.md, color: ACCENT, padding: 0, lineHeight: 1 }}>×</button>
          {bulkActions?.map((a, i) => (
            <button key={i} onClick={a.onClick} style={{ background: ACCENT, border: 'none', color: '#fff', borderRadius: T.r.sm, padding: '4px 10px', fontSize: T.type.xs, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>
              {a.icon && <span style={{ marginRight: 4 }}>{a.icon}</span>}{a.label}
            </button>
          ))}
        </div>
      )}

      {search && (
        <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '1 1 240px', minWidth: 0 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: T.type.base, color: '#9CA3AF', userSelect: 'none' }}>⌕</span>
          <input
            value={search.value}
            onChange={e => search.onChange(e.target.value)}
            onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.boxShadow = `0 0 0 3px ${ACCENT_MID}`; }}
            onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
            placeholder={search.placeholder || 'Search…'}
            style={merge(base, {
              width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 9, paddingBottom: 9,
              border: `1.5px solid ${T.border}`, borderRadius: T.r.md,
              fontSize: T.type.base, color: '#000000', background: '#FFFFFF', outline: 'none',
              fontWeight: 500,
            })}
          />
        </div>
      )}

      {filters && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{filters}</div>}

      <div style={{ flex: 1 }} />

      {exportFn && !isMobile && (
        <AoneButton variant="ghost" size="sm" onClick={exportFn}>↓ Export</AoneButton>
      )}
      {secondary?.map((a, i) => (
        <AoneButton key={i} variant="secondary" size="sm" onClick={a.onClick}>
          {a.icon && <span style={{ marginRight: 4 }}>{a.icon}</span>}{a.label}
        </AoneButton>
      ))}
      {primary && (
        <AoneButton variant="primary" size="sm" onClick={primary.onClick}>
          {primary.icon && <span style={{ marginRight: 4 }}>{primary.icon}</span>}{primary.label}
        </AoneButton>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. AoneTabs — horizontal tab strip
// ─────────────────────────────────────────────────────────────────────────────
export function AoneTabs({ tabs, active, onChange, style }) {
  const { isMobile } = useResponsive();

  // Fusion C2 — dark-pill segmented control on a white card. Active = ink-black.
  return (
    <div style={merge(base, {
      display: 'flex', gap: 2,
      background: '#FFFFFF',
      border: `0.5px solid ${T.borderStrong}`,
      borderRadius: 14, padding: 6, width: 'fit-content',
      maxWidth: '100%', overflowX: 'auto',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    }, style)}>
      {tabs.map(tab => {
        const isActive = tab.key === active;
        return (
          <button key={tab.key} onClick={() => onChange(tab.key)}
            style={merge(base, {
              padding: isMobile ? '8px 14px' : '9px 16px',
              borderRadius: 9, border: 'none',
              background: isActive ? '#1C1C1E' : 'transparent',
              color: isActive ? '#FFFFFF' : T.textSec,
              fontSize: T.type.sm, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
              whiteSpace: 'nowrap', flexShrink: 0,
              transition: `all ${T.duration.base} ${T.ease}`,
            })}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(120,120,128,0.06)'; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
          >
            {tab.icon && <span style={{ fontSize: T.type.base }}>{tab.icon}</span>}
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span style={{
                fontSize: T.type.xxs, fontWeight: 700, minWidth: 18, height: 18,
                background: isActive ? 'rgba(255,255,255,0.18)' : T.role.coral.fill,
                color: isActive ? 'rgba(255,255,255,0.85)' : T.role.coral.ink,
                borderRadius: 9,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 5px',
              }}>{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 9. AoneMobileActionCard — compact action card for mobile
// ─────────────────────────────────────────────────────────────────────────────
export function AoneMobileActionCard({ icon, title, subtitle, meta, status, actions, urgent = false, onClick, style }) {
  return (
    <div onClick={onClick} style={merge(base, {
      background: urgent ? T.card.coral.bg : T.surface,
      borderRadius: T.r.lg, padding: '14px 16px',
      border: `1px solid ${urgent ? T.card.coral.border : T.border}`,
      cursor: onClick || actions?.length ? 'pointer' : 'default',
      boxShadow: urgent ? `0 0 0 1px rgba(220,90,74,0.20), ${T.shadow.sm}` : T.shadow.sm,
    }, style)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {icon && (
          <div style={{ width: 40, height: 40, borderRadius: T.r.md, background: ACCENT_SOFT, border: `1px solid ${ACCENT_MID}`, color: ACCENT_HOVER, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.type.xl, flexShrink: 0 }}>
            {icon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ fontSize: T.type.md, fontWeight: 700, color: '#000000', lineHeight: 1.3 }}>{title}</div>
            {meta && <div style={{ fontSize: T.type.xs, color: '#111111', whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 600 }}>{meta}</div>}
          </div>
          {subtitle && <div style={{ fontSize: T.type.sm, color: '#111111', marginTop: 3, lineHeight: 1.4 }}>{subtitle}</div>}
          {status && <div style={{ marginTop: 6 }}><AoneStatusChip status={status} size="sm" /></div>}
        </div>
      </div>
      {actions?.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
          {actions.map((a, i) => (
            <AoneButton key={i} variant={a.variant || 'secondary'} size="sm" onClick={e => { e.stopPropagation(); a.onClick(); }} style={{ flex: 1 }}>
              {a.label}
            </AoneButton>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. AoneEmptyState
// ─────────────────────────────────────────────────────────────────────────────
export function AoneEmptyState({ icon = '📭', title = 'Nothing here', message, subtitle, action, inline = false, style }) {
  // `action` accepts EITHER a JSX node (React.isValidElement) OR a
  // { label, onClick } object — backward-compatible with both call styles.
  const renderAction = () => {
    if (!action) return null;
    if (React.isValidElement(action)) return <div style={{ marginTop: 8 }}>{action}</div>;
    if (typeof action === 'object' && action.label) {
      return (
        <AoneButton variant="primary" size="sm" onClick={action.onClick} style={{ marginTop: 8 }}>
          {action.label}
        </AoneButton>
      );
    }
    return null;
  };
  const body = message || subtitle;   // accept either prop name
  return (
    <div style={merge(base, {
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: inline ? '20px 12px' : '64px 24px',
      textAlign: 'center', gap: 10,
    }, style)}>
      <span style={{ fontSize: inline ? T.type.xxxl : 44 }}>{icon}</span>
      <div style={{ fontSize: inline ? T.type.base : T.type.lg, fontWeight: 700, color: '#000000' }}>{title}</div>
      {body && <div style={{ fontSize: T.type.sm, color: '#444444', maxWidth: 360, lineHeight: 1.6 }}>{body}</div>}
      {renderAction()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 11. AoneAlertBanner — full-width alert bar
// ─────────────────────────────────────────────────────────────────────────────
export function AoneAlertBanner({ variant = 'info', title, message, action, dismissible = true, onDismiss, style }) {
  const [visible, setVisible] = useState(true);

  const cfg = {
    info:    { bg: T.card.aqua.bg,     border: T.card.aqua.border,     icon: 'ℹ️',  color: T.aqua    },
    warning: { bg: T.warningBg,        border: T.warningBorder,        icon: '⚠️', color: T.warning },
    danger:  { bg: T.dangerBg,         border: T.dangerBorder,         icon: '🚨', color: T.danger  },
    success: { bg: T.successBg,        border: T.successBorder,        icon: '✅', color: T.success },
    rose:    { bg: T.card.rose.bg,     border: T.card.rose.border,     icon: '💗', color: T.rose    },
  }[variant] || {};

  if (!visible) return null;

  return (
    <div style={merge(base, {
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 18px', borderRadius: T.r.lg,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
    }, style)}>
      <span style={{ fontSize: T.type.md, flexShrink: 0 }}>{cfg.icon}</span>
      <div style={{ flex: 1 }}>
        {title && <div style={{ fontSize: T.type.base, fontWeight: 700, color: '#000000' }}>{title}</div>}
        {message && <div style={{ fontSize: T.type.sm, color: '#111111', marginTop: title ? 3 : 0, lineHeight: 1.6 }}>{message}</div>}
        {action && (
          <button onClick={action.onClick} style={merge(base, {
            marginTop: 10, background: 'none', border: `1.5px solid ${cfg.border}`,
            borderRadius: T.r.sm, padding: '5px 14px', fontSize: T.type.sm, fontWeight: 700,
            color: '#000000', cursor: 'pointer',
          })}>
            {action.label}
          </button>
        )}
      </div>
      {dismissible && (
        <button onClick={() => { setVisible(false); onDismiss?.(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: T.type.lg, color: '#000000', padding: '2px 6px', lineHeight: 1 }}>×</button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AoneButton — primary (rose) / black border secondary
// ─────────────────────────────────────────────────────────────────────────────
export function AoneButton({ children, variant = 'primary', size = 'md', onClick, disabled = false, type = 'button', style, icon }) {
  const sizeStyle = {
    sm: { padding: '7px 16px', fontSize: T.type.sm, borderRadius: T.r.md },
    md: { padding: '10px 22px', fontSize: T.type.base, borderRadius: T.r.md },
    lg: { padding: '13px 28px', fontSize: T.type.md, borderRadius: T.r.lg },
  }[size];

  // Glossy face: a top white sheen → transparent → faint bottom shade, layered
  // over the variant's base colour, gives the button a domed, extruded look.
  const GLOSS = 'linear-gradient(180deg, rgba(255,255,255,0.20), rgba(255,255,255,0) 46%, rgba(0,0,0,0.06))';
  const variantStyle = {
    primary:   { background: ACCENT, color: '#FFFFFF', border: 'none' },
    secondary: { background: 'color-mix(in srgb, var(--aone-accent, #6E1F2B) 11%, #fff)', color: 'color-mix(in srgb, var(--aone-accent, #6E1F2B) 72%, #000)', border: '1px solid color-mix(in srgb, var(--aone-accent, #6E1F2B) 22%, #fff)' },
    ghost:     { background: 'transparent', color: '#000000', border: '1.5px solid transparent' },
    danger:    { background: T.danger, color: '#FFFFFF', border: 'none' },
    // Semantic action colours (SAP-Fiori intent) — fixed regardless of module:
    // positive = approve/confirm/advance, warning = hold/attention.
    positive:  { background: '#6E1F2B', color: '#FFFFFF', border: 'none' },
    warning:   { background: '#D97706', color: '#FFFFFF', border: 'none' },
    accent:    { background: '#18181B', color: '#FFFFFF', border: 'none' },
    // card variants
    rose:      { background: T.card.rose.bg,     color: '#000000', border: `1.5px solid ${T.rose}`,
                 '--btn-edge': T.rose, '--btn-ambient': 'rgba(0,0,0,0.08)', '--btn-gloss': 'rgba(255,255,255,0.6)' },
    gold:      { background: T.card.gold.bg,     color: '#000000', border: `1.5px solid ${T.gold}`,
                 '--btn-edge': T.gold, '--btn-ambient': 'rgba(0,0,0,0.08)', '--btn-gloss': 'rgba(255,255,255,0.6)' },
    lavender:  { background: T.card.lavender.bg, color: '#000000', border: `1.5px solid ${T.lavender}`,
                 '--btn-edge': T.lavender, '--btn-ambient': 'rgba(0,0,0,0.08)', '--btn-gloss': 'rgba(255,255,255,0.6)' },
  }[variant];

  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className="aone-btn-3d"
      style={merge(base, sizeStyle, variantStyle, {
        fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        whiteSpace: 'nowrap',
        WebkitTapHighlightColor: 'transparent',
      }, style)}
    >
      {icon && <span style={{ fontSize: '1em' }}>{icon}</span>}
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AoneInput — black text, rose focus ring
// ─────────────────────────────────────────────────────────────────────────────
export function AoneInput({ label, value, onChange, placeholder, type = 'text', required, error, hint, prefix, suffix, disabled, style, inputStyle }) {
  const [focused, setFocused] = useState(false);
  const { text: labelText, showStar } = fieldLabel(label, required);

  return (
    <div style={merge(base, { display: 'flex', flexDirection: 'column', gap: 5 }, style)}>
      {label && (
        <label style={{ fontSize: T.type.xs, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: 0 }}>
          {labelText}{showStar && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {prefix && <span style={{ position: 'absolute', left: 12, fontSize: T.type.base, color: '#111111', fontWeight: 600 }}>{prefix}</span>}
        <input
          type={type} value={value}
          onChange={e => onChange?.(e.target.value)}
          placeholder={placeholder} disabled={disabled} required={required}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={merge(base, {
            width: '100%', padding: '10px 14px',
            paddingLeft: prefix ? 32 : 14,
            paddingRight: suffix ? 32 : 14,
            border: `1.5px solid ${error ? T.danger : focused ? ACCENT : T.border}`,
            borderRadius: T.r.md, fontSize: T.type.md, color: '#000000',
            background: disabled ? '#F9FAFB' : '#FFFFFF',
            outline: 'none',
            // Flat Fiori field: no inset depth; focus shows a soft accent ring only.
            boxShadow: focused ? '0 0 0 2px color-mix(in srgb, var(--aone-accent, #6E1F2B) 30%, transparent)' : 'none',
            transition: `border-color ${T.duration.fast}, box-shadow ${T.duration.fast}`,
            fontWeight: 500,
          }, inputStyle)}
        />
        {suffix && <span style={{ position: 'absolute', right: 12, fontSize: T.type.base, color: '#111111' }}>{suffix}</span>}
      </div>
      {error && <span style={{ fontSize: T.type.xs, color: T.danger, fontWeight: 600 }}>{error}</span>}
      {hint && !error && <span style={{ fontSize: T.type.xs, color: '#444444' }}>{hint}</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AoneSelect — searchable dropdown
// ─────────────────────────────────────────────────────────────────────────────
export function AoneSelect({ label, value, onChange, options, placeholder, required, disabled, style }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const opts = options || [];
  const selected = opts.find(o => String(o.value) === String(value));
  const filtered = search ? opts.filter(o => o.label?.toLowerCase().includes(search.toLowerCase())) : opts;
  const { text: labelText, showStar } = fieldLabel(label, required);

  return (
    <div ref={ref} style={merge(base, { display: 'flex', flexDirection: 'column', gap: 5, position: 'relative' }, style)}>
      {label && (
        <label style={{ fontSize: T.type.xs, fontWeight: 700, color: '#000000', textTransform: 'uppercase', letterSpacing: 0 }}>
          {labelText}{showStar && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
        </label>
      )}
      <button type="button" disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(o => !o); setSearch(''); } }}
        style={merge(base, {
          padding: '10px 14px', border: `1.5px solid ${open ? ACCENT : T.border}`, borderRadius: T.r.md,
          fontSize: T.type.md, color: selected ? '#000000' : '#9CA3AF', background: '#FFFFFF',
          outline: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: selected ? 500 : 400,
          textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          opacity: disabled ? 0.5 : 1,
        })}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : (placeholder || '— Select —')}
        </span>
        <span style={{ fontSize: T.type.xxs, color: '#9CA3AF', marginLeft: 8, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: T.r.md, boxShadow: T.shadow.lg, zIndex: T.z.dropdown, overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." onClick={e => e.stopPropagation()}
              style={{ fontFamily: T.font, width: '100%', border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: '6px 10px', fontSize: T.type.sm, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {placeholder && !search && (
              <div onClick={() => { onChange?.(''); setOpen(false); }}
                style={{ padding: '9px 14px', fontSize: T.type.sm, color: '#9CA3AF', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = ACCENT_SOFT}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {placeholder}
              </div>
            )}
            {filtered.map(o => (
              <div key={o.value} onClick={() => { onChange?.(o.value); setOpen(false); setSearch(''); }}
                style={{ padding: '9px 14px', fontSize: T.type.sm, cursor: 'pointer', borderBottom: `1px solid ${T.border}`, color: '#000000', fontWeight: String(o.value) === String(value) ? 700 : 400, background: String(o.value) === String(value) ? ACCENT_SOFT : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = ACCENT_SOFT}
                onMouseLeave={e => e.currentTarget.style.background = String(o.value) === String(value) ? ACCENT_SOFT : 'transparent'}>
                {o.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '14px', fontSize: T.type.sm, color: '#9CA3AF', textAlign: 'center' }}>No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AoneSectionHeader — inner section title
// ─────────────────────────────────────────────────────────────────────────────
export function AoneSectionHeader({ title, action, style }) {
  return (
    <div style={merge(base, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, marginTop: 4 }, style)}>
      <div style={{ fontSize: T.type.xs, fontWeight: 800, color: '#000000', textTransform: 'uppercase', letterSpacing: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 3, height: 14, background: ACCENT, borderRadius: 2 }} />
        {title}
      </div>
      {action}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AoneDivider
// ─────────────────────────────────────────────────────────────────────────────
export function AoneDivider({ label, style }) {
  return (
    <div style={merge(base, { display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0' }, style)}>
      <div style={{ flex: 1, height: 1, background: T.border }} />
      {label && <span style={{ fontSize: T.type.xs, color: '#444444', whiteSpace: 'nowrap', fontWeight: 600 }}>{label}</span>}
      {label && <div style={{ flex: 1, height: 1, background: T.border }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AoneSpinner
// ─────────────────────────────────────────────────────────────────────────────
export function AoneSpinner({ size = 24, color = ACCENT }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', border: `2px solid ${color}28`, borderTop: `2px solid ${color}`, animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Toast system
// ─────────────────────────────────────────────────────────────────────────────
export function useToasts() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((message, variant = 'info', duration = 4000) => {
    const id = Date.now();
    setToasts(p => [...p, { id, message, variant }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), duration);
  }, []);
  return [toasts, add];
}

export function AoneToastStack({ toasts = [] }) {
  const cfg = {
    info:    { bg: '#000000', icon: 'ℹ️' },
    success: { bg: T.success, icon: '✅' },
    warning: { bg: T.warning, icon: '⚠️' },
    danger:  { bg: T.danger,  icon: '🚨' },
    rose:    { bg: T.rose,    icon: '💗' },
  };
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: T.z.toast, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {toasts.map(t => {
        const c = cfg[t.variant] || cfg.info;
        return (
          <div key={t.id} style={merge(base, {
            background: c.bg, color: '#fff', padding: '13px 18px', borderRadius: T.r.lg,
            boxShadow: T.shadow.lg, display: 'flex', alignItems: 'center', gap: 10,
            fontSize: T.type.base, fontWeight: 600, maxWidth: 340, animation: 'fadeInUp 0.2s ease',
          })}>
            <span>{c.icon}</span>{t.message}
          </div>
        );
      })}
    </div>
  );
}
